import type {DndContextProps, DragCancelEvent} from '@dnd-kit/core';
import type {CSSProperties, PropsWithChildren, RefObject} from 'react';
import {createContext} from 'react';
import type {UsePanStrategy} from '@Libraries/timeline/utils';
import type {DragEndEvent, DragMoveEvent, DragStartEvent, Range} from '.';
import type {ResizeEndEvent, ResizeMoveEvent, ResizeStartEvent} from './resize';

export interface PanEndEvent {
  clientX?: number;
  clientY?: number;
  deltaX: number;
  deltaY: number;
}

export type GetSpanFromDragEvent = (event: DragStartEvent | DragEndEvent | DragCancelEvent | DragMoveEvent) => Range | null;

export type GetSpanFromResizeEvent = (event: ResizeEndEvent) => Range | null;

export type GetValueFromScreenX = (screenX: number) => number;

export type OnResizeStart = (event: ResizeStartEvent) => void;

export type OnResizeEnd = (event: ResizeEndEvent) => void;

export type OnResizeMove = (event: ResizeMoveEvent) => void;

export type OnPanEnd = (event: PanEndEvent) => void;

export type PixelsToValue = (pixels: number, range?: Range) => number;
export type ValueToPixels = (value: number, range?: Range) => number;

export interface TimelineBag {
  style: CSSProperties;
  range: Range;
  overlayed: boolean;
  onResizeEnd: OnResizeEnd;
  onResizeMove?: OnResizeMove;
  onResizeStart?: OnResizeStart;
  resizeHandleWidth: number;
  rangeGridSize?: number;
  direction: CanvasDirection;
  timelineRef: React.MutableRefObject<HTMLElement | null>;
  setTimelineRef: (element: HTMLElement | null) => void;
  sidebarWidth: number;
  sidebarRef: React.MutableRefObject<HTMLElement | null>;
  setSidebarRef: (element: HTMLElement | null) => void;
  valueToPixels: ValueToPixels;
  pixelsToValue: PixelsToValue;
  getValueFromScreenX: GetValueFromScreenX;
  getSpanFromDragEvent: GetSpanFromDragEvent;
  getSpanFromResizeEvent: GetSpanFromResizeEvent;
  isResizing: boolean;
}

export type OnRangeChanged = (updateFunction: (prev: Range) => Range) => void;

export interface GridSizeDefinition {
  value: number;
  maxRangeSize?: number;
}

export interface UseTimelineProps {
  range: Range;
  overlayed?: boolean;
  onResizeEnd: OnResizeEnd;
  resizeHandleWidth?: number;
  onResizeMove?: OnResizeMove;
  onResizeStart?: OnResizeStart;
  usePanStrategy?: UsePanStrategy;
  onRangeChanged: OnRangeChanged;
  rangeGridSizeDefinition?: number | GridSizeDefinition[];
}

export interface TimelineContextProps extends PropsWithChildren, UseTimelineProps, DndContextProps {}

export const timelineContext = createContext<TimelineBag | undefined>(undefined);

// eslint-disable-next-line @typescript-eslint/naming-convention
export const TimelineProvider = timelineContext.Provider;

export interface UseElementRefReturnObject {
  ref: RefObject<HTMLElement | null>;
  setRef: (element: HTMLElement | null) => void;
  width: number;
  direction: CanvasDirection;
}
